/**
 * MemberBanner — the member-facing banner strip (US14 / FR-057).
 *
 * The ONLY read is `useBanner()` → `ContentReaderPort.getPublishedBanner`.
 * Title and subtitle are authored content and render verbatim (no i18n key);
 * the colour arrives through the `--color-banner-*` token pair the utilities
 * `bg-banner-<color>` / `text-banner-<color>-foreground` bind to.
 *
 * Placement (contract §5): Home mounts `placement="home"` above its tabs and
 * renders both scopes; the shell mounts `placement="shell"` in the shell frame,
 * renders ONLY `scope='sitewide'`, and skips `/`
 * so Home's own strip is the single render there.
 *
 * Dismissal is a browser preference, never server state: the ids of dismissed
 * banners live in `localStorage['omaplus:dismissed-banners:v1']` (a JSON
 * `string[]`). A re-published row keeps its id, so a member who dismissed it
 * stays dismissed. A non-dismissible banner renders NO close control (absent,
 * not disabled). `null` from the port renders nothing — no wrapper, no height.
 *
 * The strip is `role="status"` named by its title; the TopBar owns the page's
 * single `role="banner"`.
 */

import type { JSX } from 'react';
import { useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';

import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { useSession } from '../session/useSession.js';
import { CloseIcon } from '../shell/icons.js';
import type { Banner, BannerColor } from './ContentReaderPort.js';
import { useBanner } from './hooks/useBanner.js';

export const DISMISSED_BANNERS_STORAGE_KEY = 'omaplus:dismissed-banners:v1';

export type MemberBannerPlacement = 'home' | 'shell';

export interface MemberBannerProps {
  readonly placement: MemberBannerPlacement;
}

function readDismissedIds(): readonly string[] {
  // Read once at module load as well as on reset: with no window (a
  // non-browser import) there is no browser preference, so nothing is
  // dismissed. The try/catch below covers storage that exists but refuses.
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(DISMISSED_BANNERS_STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeDismissedIds(ids: readonly string[]): void {
  try {
    window.localStorage.setItem(DISMISSED_BANNERS_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private mode, quota): the in-memory dismissal still
    // applies for this mount; the preference simply does not persist.
  }
}

// One dismissed-id store for EVERY mount. The shell's strip survives
// member-to-member navigation (Layout is preserved across routes), so a
// per-mount snapshot would let a banner dismissed on the Home strip return
// on the next route. The store is read through useSyncExternalStore, which
// keeps render pure and lets both mounts see one value.
const dismissedListeners = new Set<() => void>();
let dismissedSnapshot: readonly string[] = readDismissedIds();

function subscribeDismissed(listener: () => void): () => void {
  dismissedListeners.add(listener);
  return () => {
    dismissedListeners.delete(listener);
  };
}

function getDismissedSnapshot(): readonly string[] {
  return dismissedSnapshot;
}

function dismissBanner(id: string): void {
  if (dismissedSnapshot.includes(id)) {
    return;
  }
  dismissedSnapshot = [...dismissedSnapshot, id];
  writeDismissedIds(dismissedSnapshot);
  for (const listener of dismissedListeners) {
    listener();
  }
}

/** Test seam: forget every dismissal so a fresh browser can be simulated. */
export function resetDismissedBannersForTests(): void {
  dismissedSnapshot = readDismissedIds();
  for (const listener of dismissedListeners) {
    listener();
  }
}

const stripClassesByColor: Record<BannerColor, string> = {
  green: 'bg-banner-green text-banner-green-foreground',
  navy: 'bg-banner-navy text-banner-navy-foreground',
  red: 'bg-banner-red text-banner-red-foreground',
};

const stripBaseClasses = 'w-full';
// No width cap of its own: the strip fills the page column. The text keeps
// the page gutter INSIDE the strip (the same horizontal padding the column
// uses), so it is inset from the strip's coloured edge as the proto renders
// it, while the strip itself stays as wide as the column. The wider desktop
// right padding keeps the close control clear of the fixed language pill
// (DesktopLanguageSwitcher) while the banner scrolls beneath it.
const innerClasses = 'flex items-start justify-between gap-3 px-4 py-3 lg:pr-44';
const copyClasses = 'min-w-0 flex-1';
const titleClasses = 'm-0 text-sm font-bold leading-tight';
const subtitleClasses = 'mt-0.5 m-0 text-xs leading-snug opacity-90';
const closeClasses =
  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ' +
  'cursor-pointer transition-colors hover:brightness-90 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const closeIconClasses = 'h-4 w-4';

function rendersAt(banner: Banner, placement: MemberBannerPlacement, pathname: string): boolean {
  if (placement === 'home') {
    return true;
  }
  return banner.scope === 'sitewide' && pathname !== '/';
}

export function MemberBanner({ placement }: MemberBannerProps): JSX.Element | null {
  const t = useTranslate();
  const { pathname } = useLocation();
  const { active: locale } = useLanguage();
  const { state: session } = useSession();
  const banner = useBanner({ locale, enabled: session.kind !== 'anonymous' });
  const dismissedIds = useSyncExternalStore(
    subscribeDismissed,
    getDismissedSnapshot,
    getDismissedSnapshot,
  );

  const data = banner.data;
  if (data === undefined || data === null) {
    return null;
  }
  if (!rendersAt(data, placement, pathname) || dismissedIds.includes(data.id)) {
    return null;
  }

  const dismiss = (): void => {
    dismissBanner(data.id);
  };

  return (
    <div
      role="status"
      aria-label={data.title}
      className={`${stripBaseClasses} ${stripClassesByColor[data.color]}`}
    >
      <div className={innerClasses}>
        <div className={copyClasses}>
          <p className={titleClasses}>{data.title}</p>
          {data.subtitle !== null && <p className={subtitleClasses}>{data.subtitle}</p>}
        </div>
        {data.dismissible && (
          <button
            type="button"
            className={`${closeClasses} ${stripClassesByColor[data.color]}`}
            aria-label={t('banner.close')}
            onClick={dismiss}
          >
            <CloseIcon aria-hidden="true" className={closeIconClasses} />
          </button>
        )}
      </div>
    </div>
  );
}
